import { CSSProperties, FunctionComponent, memo, useEffect, useRef, useState } from 'react';

import styles from './custom-select.module.css';

import { TReactSelectOption } from '../react-select/custom-react-select.types';
import CustomSelectUnfocusedItems from './components/custom-select-unfocused-items';
import CustomSelectMenu from './components/custom-select-menu';
import CustomSelectHeader from './components/custom-select-header';
import CustomSelectFocusedItems from './components/custom-select-focused-items';
import CustomSelectInput from './components/custom-select-input';
import selectKeyDownCapture from './helpers/select-keydown-capture';
import useResetAttributes from './helpers/use-reset-attributes';
import useScrollResets from './helpers/use-scroll-resets';
import useFilteredOptions from './helpers/use-filtered-options';
import FakeBorder from './components/fake-border';
import clearHighlightedSelection from '@/utils/clear-highlighted-selection';

type TCustomSelectProps = {
  options: TReactSelectOption[];
  defaultOptions?: TReactSelectOption[];
  controlledOptions?: TReactSelectOption[];
  onFocusClosed?: (selectedOptions: TReactSelectOption[]) => void;
  onFocusChange?: (isFocused: boolean) => void;
  /** lockActiveState `true` will prevent onFocuseClosed from firing, it will not necessarily keep the menu open */
  lockActiveState: boolean;
  placeholder?: string;
  id: string;
  RenderFocusedLabelContent?: FunctionComponent<{ option: TReactSelectOption; idx: number }>;
  RenderUnfocusedLabelContent?: FunctionComponent<{
    option: TReactSelectOption;
    idx: number;
  }>;
  RenderMenuOptionContent?: FunctionComponent<{ option: TReactSelectOption; idx: number }>;
  RenderNoOptionsContent?: FunctionComponent;
  RenderHeaderLabelContent?: FunctionComponent<{ selectedOptionsCount: number }>;
  onAddNewSelect?: (inputValue: string) => void;
  labelHeight?: string;
};

const CustomSelect = memo(
  ({
    id,
    placeholder,
    options: allOptions,
    onFocusClosed,
    defaultOptions,
    controlledOptions,
    onFocusChange,
    RenderFocusedLabelContent,
    RenderUnfocusedLabelContent,
    RenderMenuOptionContent,
    RenderNoOptionsContent,
    lockActiveState: lockActiveStateProps,
    onAddNewSelect,
    labelHeight = '42px',
    RenderHeaderLabelContent,
  }: TCustomSelectProps) => {
    const selectRef = useRef<HTMLDivElement | null>(null);
    const selectedOptionsRef = useRef<HTMLDivElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const [searchValue, setSearchValue] = useState('');
    const [menuOpen, setMenuOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState<TReactSelectOption[]>(
      controlledOptions || defaultOptions || []
    );

    useEffect(() => {
      if (controlledOptions) {
        setSelectedOptions(controlledOptions);
      }
    }, [controlledOptions]);

    const selectableOptions = useFilteredOptions({
      searchValue,
      selectedOptions,
      allOptions,
    });

    const hasActivatedAtleastOnce = isFocused || lockActiveStateProps || menuOpen;
    const lockActiveState = menuOpen || isFocused || lockActiveStateProps;

    useEffect(() => {
      if (!lockActiveStateProps && !isFocused && hasActivatedAtleastOnce) {
        onFocusClosed?.(selectedOptions);
      }
    }, [isFocused, lockActiveStateProps, onFocusClosed, selectedOptions, hasActivatedAtleastOnce]);

    useEffect(() => {
      onFocusChange?.(lockActiveState);
    }, [onFocusChange, lockActiveState]);

    useScrollResets({
      selectRef,
      inputRef,
      setSearchValue,
      lockActiveState,
    });

    useResetAttributes({
      lockActiveStateProps,
      lockActiveState,
      menuOpen,
      menuRef,
      isFocused,
      selectedOptionsRef,
      setMenuOpen,
    });

    const handleToggleMenu = () => {
      if (lockActiveStateProps || !lockActiveState) return;
      setMenuOpen((prev) => !prev);
    };

    const handleRemoveClick = (option: string | number) => (e: React.MouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();
      e.preventDefault();
      setSelectedOptions((prev) => prev.filter((so) => so.value.toString() !== option.toString()));
    };

    const handlePreventDefaultClick = (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      e.preventDefault();
    };

    return (
      <>
        <div
          data-component="CustomSelect"
          ref={selectRef}
          role="combobox"
          aria-expanded={menuOpen}
          aria-label="Select options"
          className={`${styles.select} ${lockActiveState ? styles['is-focused'] : ''} ${selectedOptions?.length ? styles['has-value'] : ''}`}
          data-select-focused={lockActiveState}
          onKeyDownCapture={selectKeyDownCapture({
            lockActiveStateProps,
            lockActiveState,
            menuOpen,
            setMenuOpen,
            selectedOptionsRef,
            menuRef,
            selectRef,
            inputRef,
            selectedOptions,
            setSelectedOptions,
            onAddNewSelect,
            selectableOptions,
            allOptions,
          })}
          onPointerDownCapture={() => {
            // because we're prevent defaults in a lot of places, when the page has highlighted text
            // this entire select feature requires two clicks to get into it: one to remove highlight text, then one to enter
            // so we first need to clear all highlighted text to avoid that issue.
            // but only if we're not already "active", or the input itself won't be selectable
            if (!lockActiveState) {
              clearHighlightedSelection();
            }
          }}
        >
          <CustomSelectHeader
            handlePreventDefaultClick={handlePreventDefaultClick}
            RenderHeaderLabelContent={RenderHeaderLabelContent}
            isFocused={isFocused}
            inputRef={inputRef}
            handleToggleMenu={handleToggleMenu}
            selectedOptionsCount={selectedOptions.length}
          />

          <label
            style={{ '--custom-select-control-height': labelHeight } as CSSProperties}
            htmlFor={id}
            role="presentation"
            className={`${styles.label} ${lockActiveState ? styles['lock-active-state'] : ''}`}
            onClick={handleToggleMenu} /* this lets child element clicks */
            onPointerDown={handlePreventDefaultClick} /* this prevents duplicate triggers */
            onPointerUp={(e) => {
              if (e.target instanceof HTMLLabelElement) {
                handleToggleMenu(); /* this enures we can can still "click" this component itself */
              }
            }}
          >
            <CustomSelectFocusedItems
              handlePreventDefaultClick={handlePreventDefaultClick}
              handleToggleMenu={handleToggleMenu}
              selectedOptions={selectedOptions}
              selectedOptionsRef={selectedOptionsRef}
              RenderFocusedLabelContent={RenderFocusedLabelContent}
              lockActiveState={lockActiveState}
              handleRemoveClick={handleRemoveClick}
            />

            <CustomSelectUnfocusedItems
              RenderUnfocusedLabelContent={RenderUnfocusedLabelContent}
              selectedOptions={selectedOptions}
              selectRef={selectRef}
              lockActiveState={lockActiveState}
            />

            <CustomSelectInput
              inputRef={inputRef}
              menuRef={menuRef}
              selectedOptionsRef={selectedOptionsRef}
              id={id}
              lockActiveState={lockActiveState}
              hasSelectedOptions={!!selectedOptions.length}
              placeholder={placeholder}
              setIsFocused={setIsFocused}
              setMenuOpen={setMenuOpen}
              setSearchValue={setSearchValue}
            />

            <CustomSelectMenu
              menuOpen={menuOpen}
              menuRef={menuRef}
              selectRef={selectRef}
              handlePreventDefaultClick={handlePreventDefaultClick}
              selectableOptions={selectableOptions}
              setSelectedOptions={setSelectedOptions}
              onAddNewSelect={onAddNewSelect}
              inputRef={inputRef}
              RenderMenuOptionContent={RenderMenuOptionContent}
              RenderNoOptionsContent={RenderNoOptionsContent}
            />
          </label>
        </div>

        <FakeBorder lockActiveState={lockActiveState} menuOpen={menuOpen} />
      </>
    );
  }
);

CustomSelect.displayName = 'CustomSelect';

export default CustomSelect;
